import { useEffect, useState } from "react";
import "../styles/productdetails.css";
import ProductSection from "../components/common/ProductSection";
import axios from "axios";
import Constant from "../helper/Constant";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import BestSellingProducts from "../components/common/BestSellingProducts";
import {
  buildProductDetailsPath,
  decodeProductTokenFromPath,
  extractProductIdFromToken,
} from "../helper/productUrl";
import { FaInstagram } from "react-icons/fa";
import Seo, { DEFAULT_IMAGE, SITE_URL } from "../components/common/Seo";

const getGuestCartId = () => {
  let guestCartId = localStorage.getItem("guestCartId");

  if (!guestCartId) {
    guestCartId = `guest_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    localStorage.setItem("guestCartId", guestCartId);
  }

  return guestCartId;
};

export default function ProductDetails() {
  const navigate = useNavigate();
  const { productToken } = useParams();
  const [product, setProduct] = useState(null);
  const [variants, setVariants] = useState([]);

  const [colorArray, setColorArray] = useState([]);
  const [sizeArray, setSizeArray] = useState([]);

  const [selectedColor, setSelectedColor] = useState("");
  const [selectedSize, setSelectedSize] = useState("");
  const [hideSize, setHideSize] = useState(false);
  const [hideColour, setHideColour] = useState(false);
  const [customText, setCustomText] = useState("");
  const [showCustomAddons, setShowCustomAddons] = useState(false);
  const [customPhoto, setCustomPhoto] = useState(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: "", mobile: "" });
  const [leadLoading, setLeadLoading] = useState(false);

  const [selectedVariant, setSelectedVariant] = useState(null);

  const [activeImage, setActiveImage] = useState("");
  const [similarProducts, setSimilarProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inCart, setInCart] = useState(false);
  const [activeTab, setActiveTab] = useState("description");
  const [detailsLoading, setDetailsLoading] = useState(true);
  const [detailsError, setDetailsError] = useState("");
  const decodedUrlPayload = decodeProductTokenFromPath(productToken || "");
  const resolvedProductId = extractProductIdFromToken(productToken);
  // Filter best selling products here
  const bestSelling = similarProducts.filter(
    (item) => item.best_selling == "true"
  );

  // Upgrade legacy encrypted links immediately. This must not wait for the
  // product request: a slow or unavailable API should not leave visitors on
  // the obsolete, oversized URL.
  useEffect(() => {
    if (
      !resolvedProductId ||
      !window.location.pathname.startsWith("/product-details/")
    ) {
      return;
    }

    navigate(
      buildProductDetailsPath({
        _id: resolvedProductId,
        name: decodedUrlPayload?.productName || "product",
      }),
      { replace: true },
    );
  }, [
    decodedUrlPayload?.productName,
    navigate,
    resolvedProductId,
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);
    getDetails();
  }, [resolvedProductId]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const leadCaptured = localStorage.getItem("leadCaptured");

    setShowLeadModal(!token && leadCaptured !== "true");
  }, [resolvedProductId]);

  useEffect(() => {
    if (selectedVariant) {
      checkCartStatus();
    }
  }, [selectedVariant]);

  useEffect(() => {
    const el = document.querySelector(".pd-info");

    if (!el) return;

    const handleEnter = () => {
      document.body.classList.add("no-scroll");
    };

    const handleLeave = () => {
      document.body.classList.remove("no-scroll");
    };

    el.addEventListener("mouseenter", handleEnter);
    el.addEventListener("mouseleave", handleLeave);

    return () => {
      el.removeEventListener("mouseenter", handleEnter);
      el.removeEventListener("mouseleave", handleLeave);
    };
  }, []);

  /* GET DETAILS */

  const resolveHideFlag = (value, fallback = false) => {
    if (value === undefined || value === null || value === "") {
      return fallback;
    }

    return String(value) === "true";
  };

  const shouldHideVariantSize = (productData, size) => {
    const productName = String(productData?.name || "").trim().toLowerCase();
    const normalizedSize = String(size || "").trim().toLowerCase().replace(/\s+/g, "");

    return (
      productName === "mahadev idol" &&
      (normalizedSize === "18cm" || normalizedSize === "12cm")
    );
  };

  const findMatchingVariant = (nextColor, nextSize, variantList = variants) => {
    if (!variantList.length) return null;

    return (
      variantList.find((variant) => {
        const colorMatches = !nextColor || variant.colour === nextColor;
        const sizeMatches = !nextSize || variant.size === nextSize;
        return colorMatches && sizeMatches;
      }) ||
      variantList.find((variant) => !nextColor || variant.colour === nextColor) ||
      variantList.find((variant) => !nextSize || variant.size === nextSize) ||
      variantList[0]
    );
  };

  const getDetails = async () => {
    try {
      setDetailsLoading(true);
      setDetailsError("");
      const id = resolvedProductId;

      if (!id) {
        setDetailsError("This product link is invalid.");
        toast.error("Invalid product link");
        return;
      }

      const res = await axios.post(Constant.postUrls.getProductDetails, { id });
      const data = res.data.data;

      if (!data?._id) {
        throw new Error("Product not found");
      }

      setProduct(data);
      const variantList = Array.isArray(data.variants) ? data.variants : [];
      const legacyHide = resolveHideFlag(data.hide_size_colour, false);
      setHideSize(resolveHideFlag(data.hide_size, legacyHide));
      setHideColour(resolveHideFlag(data.hide_colour, legacyHide));
      setVariants(variantList);

      const categoryId = data.category?._id || data.category;
      if (categoryId) {
        getSimilarProducts(categoryId, data._id);
      } else {
        setSimilarProducts([]);
      }

      let colors = [];
      let sizes = [];

      variantList.forEach((v) => {
        if (v.colour && !colors.includes(v.colour)) {
          colors.push(v.colour);
        }

        if (v.size && !shouldHideVariantSize(data, v.size) && !sizes.includes(v.size)) {
          sizes.push(v.size);
        }
      });

      setColorArray(colors);
      setSizeArray(sizes);

  
      if (variantList.length > 0) {
        const selectableVariants =
          variantList.filter((variant) => !shouldHideVariantSize(data, variant.size));
        const variantsForSelection = selectableVariants.length
          ? selectableVariants
          : variantList;
        const urlVariant =
          variantsForSelection.find(
            (variant) =>
              variant._id === decodedUrlPayload?.variantId ||
              (decodedUrlPayload?.colour &&
                decodedUrlPayload?.size &&
                variant.colour === decodedUrlPayload.colour &&
                variant.size === decodedUrlPayload.size),
          ) || variantsForSelection[0];

        setSelectedColor(urlVariant.colour || "");
        setSelectedSize(urlVariant.size || "");
        setSelectedVariant(urlVariant);
        const firstImage = urlVariant.images?.[0] || data.images?.[0];
        setActiveImage(firstImage ? Constant.imageDomain + firstImage : "");
      } else if (data.images?.length) {
        setActiveImage(Constant.imageDomain + data.images[0]);
      }
    } catch (error) {
      setProduct(null);
      setDetailsError(
        error?.response?.data?.message || "This product could not be loaded.",
      );
      toast.error("Failed to load product");
    } finally {
      setDetailsLoading(false);
    }
  };

  const isCustomCategoryProduct = () => {
    const categoryTitle =
      product?.category?.title ||
      product?.category?.name ||
      product?.category ||
      "";

    return String(categoryTitle).toLowerCase().includes("custom");
  };
  

  /* SIMILAR PRODUCTS */

  const getSimilarProducts = async (catId, currentProductId) => {
    try {
      const res = await axios.post(
        `${Constant.postUrls.getSimilarProducts}?page=1&limit=5&catId=${catId}`
      );

      const products = Array.isArray(res.data?.data?.docs)
        ? res.data.data.docs
        : [];
      setSimilarProducts(
        products.filter((item) => item?._id !== currentProductId),
      );
    } catch {
      setSimilarProducts([]);
    }
  };

  /* HANDLE VARIANT */

  const handlePriceFilter = (color, size) => {
    const found = findMatchingVariant(color, size);

    if (found) {
      setSelectedColor(found.colour || "");
      setSelectedSize(found.size || "");
      setSelectedVariant(found);
      const firstImage = found.images?.[0] || product?.images?.[0];
      setActiveImage(firstImage ? Constant.imageDomain + firstImage : "");
    }
  };

  useEffect(() => {
    if (!product?._id) return;

    const nextPath = buildProductDetailsPath(product, selectedVariant);

    if (window.location.pathname !== nextPath) {
      navigate(nextPath, { replace: true });
    }
  }, [navigate, product, selectedVariant]);

  /* CHECK CART */

  const checkCartStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return setInCart(false);

      const res = await axios.get(Constant.postUrls.getMyCartItems, {
        headers: { Authorization: token },
      });

      const items = res.data.data || [];

      const exists = items.some(
        (item) =>
          item.product_id?._id === product._id &&
          (item.variant_id?._id || null) === (selectedVariant?._id || null)
      );

      setInCart(exists);
    } catch {}
  };

  /* ADD TO CART */

  const handleAddToCart = async () => {
    const token = localStorage.getItem("token");

    // if (!token) {
    //   localStorage.setItem("redirectAfterLogin", window.location.pathname);
    //   toast.error("Please login first");
    //   setTimeout(() => (window.location.href = "/login"), 1200);
    //   return;
    // }

    // fallback if variant not set
    let variantToUse = selectedVariant;

    if (!variantToUse && variants.length > 0) {
      variantToUse = variants[0];
      setSelectedVariant(variantToUse);
    }

    try {
      setLoading(true);
      const fd = new FormData();
      fd.append("product_id", product._id);
      fd.append("variant_id", variantToUse?._id || "");
      fd.append("quantity", 1);
      fd.append("custom_text", customText.trim());

      if (!token) {
        fd.append("guest_id", getGuestCartId());
      }

      if (customPhoto) {
        fd.append("custom_photo", customPhoto);
      }

      await axios.post(Constant.postUrls.addToCart, fd, {
        headers: token ? { Authorization: token } : {},
      });

      toast.success("Added to cart");
      setInCart(true);

      // trigger header update
      window.dispatchEvent(new Event("cartUpdated"));

    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to add to cart");
    } finally {
      setLoading(false);
    }
  };
  const handleGoToCart = () => {
    window.location.href = "/cart";
  };

  /*  LOWEST PRICE LOGIC */

  const lowestVariantPrice =
    variants.length > 0
      ? Math.min(...variants.map((v) => Number(v.price || 0)))
      : product?.price || 0;

  const lowestVariantOfferPrice =
    variants.length > 0
      ? Math.min(
          ...variants.map((v) => Number(v.offer_price || v.price || 0))
        )
      : product?.offer_price || 0;

  const price = selectedVariant?.price || lowestVariantPrice;
  const offerPrice = selectedVariant?.offer_price || lowestVariantOfferPrice;

  const discount =
    offerPrice > 0 && price > 0
      ? Math.round(((price - offerPrice) / price) * 100)
      : 0;

  const displayImages =
    selectedVariant?.images || product?.images || [];

  const getExternalLink = (link) => {
    if (!link) return "";

    const trimmedLink = String(link).trim();

    if (!trimmedLink) return "";

    return /^https?:\/\//i.test(trimmedLink)
      ? trimmedLink
      : `https://${trimmedLink}`;
  };

  const handleReelClick = () => {
    const reelLink = getExternalLink(product?.insta_reel);

    if (reelLink) {
      window.open(reelLink, "_blank", "noopener,noreferrer");
    }
  };

  const handleLeadMobileChange = (value) => {
    const mobile = value.replace(/\D/g, "").slice(0, 10);
    setLeadForm((prev) => ({ ...prev, mobile }));
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();

    const name = leadForm.name.trim();
    const mobile = leadForm.mobile.trim();

    if (!name) {
      toast.error("Please enter your name");
      return;
    }

    if (mobile.length !== 10) {
      toast.error("Please enter a valid 10 digit mobile number");
      return;
    }

    try {
      setLeadLoading(true);
      await axios.post(Constant.postUrls.addLead, {
        name,
        mobile,
        product_id: product?._id,
        source: "product_details",
      });

      localStorage.setItem("leadCaptured", "true");
      setShowLeadModal(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save details");
    } finally {
      setLeadLoading(false);
    }
  };

  const plainDescription = String(product?.description || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const metaDescription = plainDescription
    ? plainDescription.slice(0, 155)
    : "Explore customised 3D printed gifts, personalised products, and made-to-order creations from The 3D Creator.";
  const productImage =
    displayImages.length > 0
      ? `${Constant.imageDomain}${displayImages[0]}`
      : DEFAULT_IMAGE;
  const productSchema = product
    ? {
        "@context": "https://schema.org",
        "@type": "Product",
        name: product.name,
        description: metaDescription,
        image: displayImages.map((image) => `${Constant.imageDomain}${image}`),
        brand: {
          "@type": "Brand",
          name: "The 3D Creator",
        },
        sku: product._id,
        category:
          product?.category?.title || product?.category?.name || "3D Printed Products",
        offers: {
          "@type": "Offer",
          priceCurrency: "INR",
          price: Number(offerPrice || price || 0),
          availability:
            Number(selectedVariant?.quantity ?? product?.quantity ?? 0) > 0
              ? "https://schema.org/InStock"
              : "https://schema.org/OutOfStock",
          url: `${SITE_URL}${window.location.pathname}`,
        },
      }
    : null;


    const renderTabContent = () => {
    switch (activeTab) {
      case "description":
        return (
          <div className="tab-panel-content">
            {product?.description ? (
              <div
                dangerouslySetInnerHTML={{
                  __html: product.description,
                }}
              />
            ) : (
              <p>No description available for this product.</p>
            )}
          </div>
        );
        
      case "reviews":
        return (
          <div className="tab-panel-content">
            {product?.reviews ? (
              <div
                dangerouslySetInnerHTML={{
                  __html: product.reviews,
                }}
              />
            ) : (
              <>
                <p><strong>No reviews yet.</strong></p>
                <p>
                  Be the first to review this product after your purchase.
                </p>
              </>
            )}
          </div>
        );
      
      case "shipping":
        return (
          <div className="tab-panel-content">
            <p>Free shipping available across India.</p>
            <p>Order processing starts after photo review and confirmation.</p>
            <p>3D design approval is required before final production.</p>
            <p>Estimated delivery timeline is 3 to 4 weeks.</p>
            <p>
              Since this is a personalized product, returns are generally not
              accepted once production begins.
            </p>
            <p>
              In case of transit damage or wrong item delivery, support will be
              provided after verification.
            </p>
          </div>
        );

      case "additional":
        return (
          <div className="tab-panel-content">
            <p><strong>Product Type:</strong> Personalized 3D Miniature</p>
            <p><strong>Style Options:</strong> Single Miniature / Couple Miniature</p>
            <p><strong>Printing Material:</strong> Biodegradable PLA</p>
            <p><strong>Finish:</strong> Hand painted layered color finish</p>
            <p><strong>Use Case:</strong> Gifts, anniversary keepsakes, custom décor</p>
            <p><strong>Customization:</strong> Based on uploaded customer photos</p>
          </div>
        );

      default:
      return null;
    }
  };

  return (
    <>
      <Seo
        title={
          product?.name
            ? `${product.name} | Custom 3D Printed Gift | The 3D Creator`
            : "Custom 3D Printed Product Details | The 3D Creator"
        }
        description={metaDescription}
        keywords={`${product?.name || "custom 3d printed product"}, custom 3d printed gift, personalised gifting, corporate gifting, bulk 3d print orders`}
        pathname={window.location.pathname}
        image={productImage}
        type="product"
        structuredData={productSchema}
      />
      <div className="pd-container">
      {detailsLoading ? (
        <div className="pd-status" role="status">Loading product...</div>
      ) : null}
      {!detailsLoading && detailsError ? (
        <div className="pd-status pd-status-error" role="alert">
          <h2>Product unavailable</h2>
          <p>{detailsError}</p>
          <button type="button" onClick={() => navigate("/products")}>
            Browse products
          </button>
        </div>
      ) : null}
        {/* {showLeadModal ? (
    <div className="pd-container"> */}
      {/* {showLeadModal ? (
        <div className="pd-lead-overlay">
          <form className="pd-lead-modal" onSubmit={handleLeadSubmit}>
            <h3>Enter Your Details</h3>
            <input
              type="text"
              value={leadForm.name}
              onChange={(e) =>
                setLeadForm((prev) => ({ ...prev, name: e.target.value }))
              }
              placeholder="Name"
            />
            <input
              type="tel"
              value={leadForm.mobile}
              onChange={(e) => handleLeadMobileChange(e.target.value)}
              placeholder="Mobile Number"
            />
            <button type="submit" disabled={leadLoading}>
              {leadLoading ? "Saving..." : "Submit"}
            </button>
          </form>
        </div>
      ) : null} */}
      {product && (
        <>
        <div className="row pd-top pd-card g-4">
          <div className="col-lg-4 col-md-6 col-12">
            {/* IMAGE */}
            <div className="pd-image-block">
              <div className="pd-main-image">
                <img src={activeImage} alt="" />
              </div>

              <div className="pd-thumbnails-bottom">
                {/*PRODUCT IMAGES (no variant selection) */}
                {product?.images?.map((img, i) => (
                  <img
                    key={`product-${i}`}
                    src={Constant.imageDomain + img}
                    className={
                      activeImage === Constant.imageDomain + img ? "active" : ""
                    }
                    onClick={() => {
                      setActiveImage(Constant.imageDomain + img);
                    }}
                  />
                ))}

                {/*VARIANT IMAGES (with _id selection) */}
                {variants.map((variant) =>
                  (variant.images || []).map((img, i) => (
                    <img
                      key={`${variant._id}-${i}`}
                      src={Constant.imageDomain + img}
                      className={
                        activeImage === Constant.imageDomain + img ? "active" : ""
                      }
                      onClick={() => {
                        const fullImg = Constant.imageDomain + img;
                        setActiveImage(fullImg);

                        //set variant using _id (correct way)
                        setSelectedVariant(variant);
                        setSelectedColor(variant.colour || "");
                        setSelectedSize(variant.size || "");
                      }}
                    />
                  ))
                )}

                {product?.insta_reel ? (
                  <button
                    type="button"
                    className="pd-reel-thumbnail"
                    onClick={handleReelClick}
                    aria-label="Open Instagram reel"
                  >
                    <FaInstagram />
                    <span>Reel</span>
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          <div className="col-lg-5 col-md-6 col-12">
            {/* INFO */}
            <div className="pd-info">
              <h1>{product.name}</h1>
              <p className="pd-sku">SKU ID: {product._id}</p>

            {/*  PRICE */}
            <div className="pd-price-box">
              <span className="offer">
                ₹{offerPrice > 0 ? offerPrice : price}
                {!selectedVariant && variants.length > 1}
              </span>

              {offerPrice > 0 && price > offerPrice && (
                <span className="actual">₹{price}</span>
              )}

              {discount > 0 && (
                <span className="discount">{discount}% OFF</span>
              )}
            </div>

            {variants.length > 0 && (!hideSize || !hideColour) && (
              <>
                {!hideSize && sizeArray.length > 0 && (
                  <div className="pd-section">
                    <h4>Size</h4>
                    <div className="pd-sizes">
                      {sizeArray.map((size) => (
                        <button
                          key={size}
                          className={selectedSize === size ? "active" : ""}
                          onClick={() =>
                            handlePriceFilter(
                              hideColour ? "" : selectedColor || colorArray[0],
                              size
                            )
                          }
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {!hideColour && colorArray.length > 0 && (
                  <div className="pd-section">
                    <h4>Color</h4>
                    <div className="pd-sizes">
                      {colorArray.map((color) => (
                        <button
                          key={color}
                          className={selectedColor === color ? "active" : ""}
                          onClick={() =>
                            handlePriceFilter(
                              color,
                              hideSize ? "" : selectedSize || sizeArray[0]
                            )
                          }
                        >
                          {color}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

              </>
            )}

            {isCustomCategoryProduct() ? (
              <div className="pd-section">
                <h4>Upload Custom Photo</h4>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setCustomPhoto(e.target.files?.[0] || null)}
                  style={{
                    width: "100%",
                    padding: "12px 0",
                  }}
                />
              </div>
            ) : null}

            <div className="pd-section">
              <button
                type="button"
                className="pd-custom-addons-btn"
                onClick={() => setShowCustomAddons((value) => !value)}
              >
                Custom Addons
              </button>

              {showCustomAddons ? (
                <div className="pd-custom-addon-panel">
                  <input
                    type="text"
                    value={customText}
                    onChange={(e) => setCustomText(e.target.value)}
                    placeholder="Add a Name"
                    className="pd-custom-addon-input"
                  />
                  <p className="pd-custom-addon-note">
                    150 Rupees will be charge for customization
                  </p>
                </div>
              ) : null}
            </div>


            {/* ACTION */}
            <div className="pd-actions">
              {/* {inCart ? (
                <button className="go-cart" onClick={handleGoToCart}>
                  Go to Cart
                </button>
              ) : ( */}
                <button
                  className="add-cart"
                  onClick={handleAddToCart}
                  disabled={loading}
                >
                  {loading ? "Adding..." : "Add to Cart"}
                </button>
              {/* )} */}
            </div>

            {/* DESCRIPTION */}
            

            {/* <div className="pd-description">
              <h3>Product Details</h3>
              <div
                dangerouslySetInnerHTML={{
                  __html: product.description,
                }}
              />
            </div> */}
            </div>
          </div>

          <div className="col-lg-3 col-md-12 col-12">
            {/* Best Selling Section */}
            {bestSelling.length > 0 && (
              <div className="best-selling-wrapper">
                <div className="section-header">
                  <h5>Best Selling Products</h5>
                </div>

                <div className="best-selling-list">
                  {bestSelling.map((item) => (
                    <BestSellingProducts key={item._id} product={item} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        {/* <div className="product-tabs-content">
          {renderTabContent()}
        </div>  */}
        <div className="product-tabs-sectionDetails">
          <div className="product-tabs-header">
            <button
              className={`tab-btn ${activeTab === "description" ? "active" : ""}`}
              onClick={() => setActiveTab("description")}
            >
              Description
            </button>

            <button
              className={`tab-btn ${activeTab === "reviews" ? "active" : ""}`}
              onClick={() => setActiveTab("reviews")}
            >
              Review(s)
            </button>

            <button
              className={`tab-btn ${activeTab === "shipping" ? "active" : ""}`}
              onClick={() => setActiveTab("shipping")}
            >
              Shipping &amp; Returns
            </button>

            <button
              className={`tab-btn ${activeTab === "additional" ? "active" : ""}`}
              onClick={() => setActiveTab("additional")}
            >
              Additional information
            </button>
          </div>

          <div className="product-tabs-content">
            {renderTabContent()}
          </div>
        </div>
        </>

      )}
        <ProductSection
          title="Similar Products"
          viewAllLink="/products"
          products={similarProducts}
        />
      </div>
    </>
  );
}
